import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { CircleNotch, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProcessingPanel({ project, reload }) {
  const id = project.id;
  const scene = project.selected_scene;
  const [job, setJob] = useState(project.processing_job);
  const timer = useRef(null);

  useEffect(() => {
    const status = job?.status;
    if (status && !["Completed", "Failed", "Cancelled"].includes(status)) {
      timer.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/projects/${id}/processing/status`);
          setJob(data.processing_job);
          if (["Completed", "Failed"].includes(data.processing_job?.status)) {
            clearInterval(timer.current);
            reload();
            if (data.processing_job.status === "Completed") toast.success("Processing complete - mineral maps & targets generated");
            else toast.error("Processing failed");
          }
        } catch (_) { /* ignore */ }
      }, 3500);
    }
    return () => clearInterval(timer.current);
  }, [job?.status, id]);

  const start = async () => {
    if (!scene) return toast.error("Select a satellite scene first");
    try {
      await api.post(`/projects/${id}/processing/start`);
      setJob({ status: "Queued", stage: "Queued", progress: 0 });
      toast.info("Processing job queued");
    } catch (e) { toast.error(apiError(e)); }
  };

  const running = job && !["Completed", "Failed", "Cancelled"].includes(job.status);
  const stages = ["Streaming satellite bands", "Reflectance scaling & AOI clip", "Computing spectral indices",
    "Building prospectivity model", "Generating targets"];

  return (
    <div className="space-y-4">
      {!scene ? (
        <p className="text-sm text-muted-foreground">Select a satellite scene in the previous step to enable processing.</p>
      ) : (
        <div className="border border-border rounded-md p-3">
          <p className="overline">Selected Scene</p>
          <div className="font-mono text-xs mt-1 truncate">{scene.scene_id}</div>
          <div className="text-xs text-muted-foreground mt-1">{scene.platform} · {(scene.datetime || "").slice(0, 10)} · ☁ {scene.cloud_cover}%</div>
        </div>
      )}

      <Button className="w-full" onClick={start} disabled={running || !scene} data-testid="proc-start">
        {running ? <><CircleNotch size={16} className="mr-1 animate-spin" />Processing…</> : "Start Pre-processing & Analysis"}
      </Button>

      {job && (
        <div className="space-y-3" data-testid="proc-status">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{job.stage || job.status}</span>
            <Badge className={job.status === "Completed" ? "bg-priority-low text-white" : job.status === "Failed" ? "bg-destructive text-white" : ""} variant="outline">{job.status}</Badge>
          </div>
          <Progress value={job.progress || 0} className="h-2" />
          <div className="space-y-1.5">
            {stages.map((s, i) => {
              const done = (job.progress || 0) >= (i + 1) * 18 || job.status === "Completed";
              const active = job.stage === s;
              return (
                <div key={s} className={`flex items-center gap-2 text-xs ${done ? "text-priority-low" : active ? "text-primary" : "text-muted-foreground"}`}>
                  {done ? <CheckCircle size={14} weight="fill" /> : active ? <CircleNotch size={14} className="animate-spin" /> : <span className="w-3.5 h-3.5 rounded-full border border-current inline-block" />}
                  {s}
                </div>
              );
            })}
          </div>
          {job.error && (
            <div className="text-xs text-destructive flex items-start gap-1 border border-destructive/40 rounded p-2">
              <Warning size={14} className="mt-0.5" /><span className="font-mono">{job.error}</span>
            </div>
          )}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground font-mono">
        Processing streams real Sentinel-2 COG bands, applies AOI clipping and cloud/vegetation masking, then computes spectral indices on the actual pixels.
      </p>
    </div>
  );
}
